import { useCallback, useEffect, useState } from 'react';
import { PAGINATION } from '@erpgold/shared';
import {
  customerService,
  Customer,
  CustomerFilters,
  CreateCustomerDto,
  UpdateCustomerDto,
} from '../services/customer.service';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const useCustomers = (initialFilters: CustomerFilters = {}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<CustomerFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState<PaginationMeta>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerService.getCustomers(filters);
      setCustomers(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch customers'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const createCustomer = async (data: CreateCustomerDto) => {
    const created = await customerService.createCustomer(data);
    await fetchCustomers();
    return created;
  };

  const updateCustomer = async (id: string, data: UpdateCustomerDto) => {
    const updated = await customerService.updateCustomer(id, data);
    setCustomers((prev) => prev.map((c) => (c.customer_id === id ? { ...c, ...updated } : c)));
    return updated;
  };

  const deleteCustomer = async (id: string) => {
    await customerService.deleteCustomer(id);
    setCustomers((prev) => prev.filter((c) => c.customer_id !== id));
    setMeta((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
  };

  return {
    customers,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchCustomers,
    createCustomer,
    updateCustomer,
    deleteCustomer,
  };
};

export const useCustomerDetail = (id: string) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchCustomer = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await customerService.getCustomerById(id);
      setCustomer(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch customer'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  return { customer, loading, error, refetch: fetchCustomer };
};
